/**
 * Testi legali che il cliente deve accettare prima di pagare.
 *
 * Vivono come colonne di user_public_profile e sono di chi incassa: l'agenzia per i suoi
 * membri, l'advisor autonomo per se' stesso.
 * Il frontend non ricalcola questa regola — arriva gia' risolta dal backend
 * (get_my_legal_settings, get_itinerary_legal_settings, get-shared-itinerary).
 */
export interface ILegalSettings {
  owner_id?: string | null;
  legal_name?: string | null;
  legal_email?: string | null;
  legal_address?: string | null;
  tax_id?: string | null;
  /** HTML del RichTextEditor: va reso con RichTextContent, non come testo. */
  cancellation_policy?: string | null;
  privacy_policy?: string | null;
  terms_conditions?: string | null;
}

/**
 * Stato di un documento nella pagina Settings.
 * Per l'agenzia e l'advisor autonomo `overridden` e `outdated` sono sempre false.
 */
export interface ILegalDocumentState {
  /** Testo effettivo: la personalizzazione del membro, altrimenti quello dell'agenzia. */
  value: string | null;
  agency_value: string | null;
  /** Il membro ha una versione propria di questo documento. */
  overridden: boolean;
  /** L'agenzia ha riscritto il documento dopo la personalizzazione del membro. */
  outdated: boolean;
  agency_updated_at: string | null;
  based_on: string | null;
}

/** Riepilogo di un membro, visibile solo all'agenzia. */
export interface ILegalMemberStatus {
  id: string;
  name: string | null;
  overridden: LegalDocumentKey[];
  outdated: LegalDocumentKey[];
}

/** I dati anagrafici obbligatori nella pagina Settings. */
export interface ILegalSettingsForm extends ILegalSettings {
  /** true per il membro del team: anagrafica dell'agenzia, documenti ereditati o personalizzati. */
  inherited?: boolean;
  /** Anagrafica e documenti "di chi incassa": agenzia o advisor autonomo. */
  can_edit?: boolean;
  /** Il membro puo' personalizzare i documenti, non l'anagrafica. */
  can_override?: boolean;
  owner_name?: string | null;
  updated_at?: string | null;
  documents?: Record<LegalDocumentKey, ILegalDocumentState>;
  /** Solo per l'agenzia. */
  members?: ILegalMemberStatus[] | null;
}

/**
 * Personalizzazioni da salvare: stringa = versione del membro, null = ripristina il
 * testo dell'agenzia. Le chiavi assenti restano come sono — e mandare una chiave vuol
 * dire "rivista rispetto alla versione attuale dell'agenzia", quindi solo quelle toccate.
 */
export type LegalOverridesPatch = Partial<Record<LegalDocumentKey, string | null>>;

/**
 * C'e' qualcosa da accettare solo se esiste almeno un documento.
 *
 * Stessa condizione applicata dalla edge function: se qui fosse piu' larga il cliente
 * vedrebbe una checkbox che il server non richiede, se fosse piu' stretta il pulsante
 * partirebbe verso un `legal_not_accepted`.
 */
export const hasLegalDocuments = (legal?: ILegalSettings | null): boolean =>
  !!(legal?.cancellation_policy || legal?.privacy_policy || legal?.terms_conditions);

/** I documenti effettivamente compilati, nell'ordine in cui vanno mostrati. */
export type LegalDocumentKey = 'privacy_policy' | 'terms_conditions' | 'cancellation_policy';

export const LEGAL_DOCUMENT_ORDER: LegalDocumentKey[] = [
  'privacy_policy',
  'terms_conditions',
  'cancellation_policy',
];

export const filledLegalDocuments = (legal?: ILegalSettings | null): LegalDocumentKey[] =>
  LEGAL_DOCUMENT_ORDER.filter((key) => !!legal?.[key]);
